import {
  SxProps,
  Container,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
  styled,
} from '@mui/material'
import { useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { IContentTableItem, TwoColumnList } from '@shared/ui'

import { StyledLink } from './styled'

interface IProps {
  sx?: SxProps
}

interface IItemWrapperProps {
  elHeight: number
}

const ItemWrapper = styled('div')<IItemWrapperProps>(({ theme, elHeight }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 0),
  borderBottom: 'solid 1px #D4D8D9',
  [theme.breakpoints.up('md')]: {
    height: `${elHeight}px`,
    flexDirection: 'row',
    alignItems: 'center',
  },
}))

export function KeyFundFacts({ sx }: IProps) {
  const { t } = useTranslation('widgets')
  const theme = useTheme()
  const mdAndUp = useMediaQuery(theme.breakpoints.up('md'))

  // Heights for each item (random values for now)
  const itemHeights = [
    { xs: 'auto', md: 92 },
    { xs: 'auto', md: 92 },
    { xs: 'auto', md: 156 },
    { xs: 'auto', md: 246 },
    { xs: 'auto', md: 92 },
    { xs: 'auto', md: 92 },
    { xs: 'auto', md: 92 },
    { xs: 'auto', md: 156 },
    { xs: 'auto', md: 152 },
    { xs: 'auto', md: 94 },
    { xs: 'auto', md: 92 },
  ]

  const list: IContentTableItem[] = [...Array(11)].map((_, index) => ({
    contentKey: t(`for investors.key-fund-facts.list.${index}.title` as never),
    contentValue: (
      <Trans
        ns="widgets"
        i18nKey={
          `for investors.key-fund-facts.list.${index}.description` as never
        }
        components={{
          p: <div style={{ margin: 0 }} />,
          ul: <ul style={{ margin: 0, paddingLeft: '16px' }} />,
          li: (
            <Box
              component="li"
              sx={{
                ml: 1.5,
                listStylePosition: 'inside',
                whiteSpace: 'pre-line',
              }}
            />
          ),
        }}
      />
    ),
  }))

  const itemsList = useMemo(() => {
    const items = []

    for (let index = 0; index < list.length; index++) {
      const item = list[index]

      items.push(
        <ItemWrapper
          key={item.contentKey}
          elHeight={itemHeights[index]?.md ?? 0}
        >
          <Typography
            maxWidth={140}
            variant={mdAndUp ? 'text18Bold' : 'text16Semibold'}
          >
            {item.contentKey}
          </Typography>

          <Box
            textAlign={mdAndUp ? 'right' : 'left'}
            maxWidth={400}
            sx={{
              fontSize: mdAndUp ? '18px' : '15px',
              fontWeight: mdAndUp ? 400 : 400,
              lineHeight: mdAndUp ? 1.5 : 1.4,
              '& p': {
                margin: 0,
                fontSize: 'inherit',
                fontWeight: 'inherit',
                lineHeight: 'inherit',
              },
              '& ul': {
                margin: 0,
                paddingLeft: '16px',
              },
              '& li': {
                fontSize: 'inherit',
                fontWeight: 'inherit',
                lineHeight: 'inherit',
              },
            }}
          >
            {item.contentValue}
          </Box>
        </ItemWrapper>
      )
    }

    return items
  }, [list, itemHeights, mdAndUp, t])

  // Create 2D array for TwoColumnList: [5, 6] items per column
  const columnsData = [
    itemsList.slice(0, 5), // First column: 5 items
    itemsList.slice(5, 11), // Second column: 6 items
  ]

  return (
    <Container maxWidth="lg" component="section" id="fund-facts" sx={sx}>
      <Typography variant="h2" mb={{ xs: 3.5, md: 6 }}>
        {t('for investors.key-fund-facts.title')}
      </Typography>

      <TwoColumnList
        items={columnsData}
        height={{ md: 678 }}
        columnGap={mdAndUp ? 7.5 : 0}
      />

      <Box
        textAlign={mdAndUp ? 'end' : 'start'}
        ml={{ xs: 2, md: 0 }}
        sx={{
          fontSize: mdAndUp ? '16px' : '15px',
          fontWeight: 400,
          lineHeight: mdAndUp ? 1.5 : 1.4,
          margin: 0,
          '& a': {
            color: 'inherit',
            textDecoration: 'underline',
          },
        }}
      >
        <Trans
          ns="widgets"
          i18nKey="for investors.key-fund-facts.AVANT-link"
          components={{
            a: (
              <StyledLink
                href="https://www.avantfunds.cz/cs/informacni-povinnost/"
                target="_blank"
              />
            ),
          }}
        />
      </Box>
    </Container>
  )
}
